import type { ReactNode } from 'react';

import { RiCalendarLine, RiTimeLine } from '@remixicon/react';
import classNames from 'classnames';

import { Text } from '@spotify-internal/encore-web';
import { DateUtils } from '@spotify/confidence-core-react';
import { getTypeOrNull } from '@spotify/confidence-graphql';
import {
  IssueAssigneePicker,
  IssueAutonomyLevelPicker,
  IssueFragment,
  IssueKindPicker,
  IssuePriorityPicker,
  IssueStatePicker,
  IssueSurfacesPicker,
  isIssueAgentRunning,
  useReopenIssue,
  useStartIssue,
  useUpdateIssue,
} from '@spotify/plugin-confidence-issues-react';

import {
  type IssuePropertyTrackingContext,
  useIssueClosure,
  useTrackIssuePropertyUpdate,
} from '../../hooks';
import { getIssueTheme } from '../../utils/getIssueMetadata';
import { isIssueSnoozed } from '../../utils/isIssueSnoozed';
import { getClosureExplanation } from '../IssueCloseDialog';

import styles from './IssueProperties.module.css';

type IssuePropertiesProps = {
  canEdit: boolean;
  children?: ReactNode;
  issue: IssueFragment;
  propertiesClassName: string;
  trackingContext?: IssuePropertyTrackingContext;
};

type IssueControlProps = {
  canEdit: boolean;
  issue: IssueFragment;
  trackingContext?: IssuePropertyTrackingContext;
};

const IssuePropertyLabel = ({ label }: { label: string }) => (
  <Text
    as="span"
    className={styles.propertyLabel}
    variant="bodySmall"
    semanticColor="textSubdued"
  >
    {label}
  </Text>
);

const IssueProperty = ({
  children,
  className,
  label,
}: {
  children: ReactNode;
  className?: string;
  label: string;
}) => (
  <div className={classNames(styles.property, className)}>
    <IssuePropertyLabel label={label} />
    <div className={styles.propertyValue}>{children}</div>
  </div>
);

export const IssueProperties = ({
  canEdit,
  children,
  issue,
  propertiesClassName,
  trackingContext,
}: IssuePropertiesProps) => {
  const assignee = getTypeOrNull(issue.assignee);
  const issueTheme = getIssueTheme(issue);
  const { updateIssue, isUpdating } = useUpdateIssue({
    onError: 'post-alert',
  });
  const trackPropertyUpdate = useTrackIssuePropertyUpdate(issue.name, {
    context: trackingContext,
  });
  const {
    requestClose,
    isMutating: isClosing,
    closeDialog,
  } = useIssueClosure({
    onClosed: closeReason =>
      trackPropertyUpdate('state', `closed:${closeReason}`),
  });
  const closureExplanation = getClosureExplanation(issue);
  const { reopenIssue, isMutating: isReopening } = useReopenIssue({
    onError: 'post-alert',
  });
  const { startIssue, isMutating: isStarting } = useStartIssue({
    onError: 'post-alert',
  });

  const isStateUpdating = isClosing || isReopening || isStarting;

  return (
    <div
      className={propertiesClassName}
      data-track-scope="group.issue-properties"
      data-track-data-issue={issue.name}
    >
      <IssueProperty label="Kind">
        <IssueKindPicker
          disabled={!canEdit || isUpdating}
          value={issue.kind}
          onChange={kind => {
            trackPropertyUpdate('kind', kind);
            void updateIssue(
              {
                name: issue.name,
                kind,
              },
              'kind',
            );
          }}
         data-track-source-anchor="9645c47853;issue-properties-issue-kind-picker"/>
      </IssueProperty>
      <IssueProperty label="Priority">
        <IssuePriorityPicker
          assessment={issue.assessment}
          disabled={!canEdit || isUpdating}
          justification={issue.priority?.justification}
          value={issue.priority?.level}
          onChange={level => {
            trackPropertyUpdate('priority', level);
            void updateIssue(
              {
                name: issue.name,
                priority: {
                  humanEdited: true,
                  justification: '',
                  level,
                },
              },
              'priority',
            );
          }}
         data-track-source-anchor="77ea3e3035;issue-properties-issue-priority-picker"/>
      </IssueProperty>
      <IssueProperty className={styles.opticallyCenteredProperty} label="State">
        <IssueStatePicker
          canEdit={canEdit}
          state={issue.state}
          closeReason={issue.closeReason}
          isAgentRunning={isIssueAgentRunning(issue)}
          isLoading={isStateUpdating}
          isSnoozed={isIssueSnoozed(issue.snoozedUntilTime)}
          onStart={() => {
            trackPropertyUpdate('state', 'working');
            void startIssue(issue.name);
          }}
          onClose={closeReason => {
            requestClose(issue, closeReason);
          }}
          onReopen={() => {
            trackPropertyUpdate('state', 'waiting');
            void reopenIssue(issue.name);
          }}
        />
      </IssueProperty>
      <IssueProperty
        className={styles.opticallyCenteredProperty}
        label="Assignee"
      >
        <IssueAssigneePicker
          assignee={assignee}
          disabled={isUpdating}
          onChange={assigneeName => {
            trackPropertyUpdate('assignee', assigneeName ?? 'unassigned');
            void updateIssue(
              {
                name: issue.name,
                assignee: assigneeName,
              },
              'assignee',
            );
          }}
         data-track-source-anchor="486173d569;issue-properties-issue-assignee-picker"/>
      </IssueProperty>
      {closureExplanation && (
        <IssueProperty label="Closure reason">
          <Text as="span" variant="bodySmall">
            {closureExplanation}
          </Text>
        </IssueProperty>
      )}
      {closeDialog}
      {children}
      {issueTheme && (
        <IssueProperty label="Theme">
          <Text as="span" variant="bodySmall">
            {issueTheme.displayName}
          </Text>
        </IssueProperty>
      )}
      <IssueProperty label="Created">
        <RiCalendarLine size={16} aria-hidden />
        <Text as="span" variant="bodySmall">
          {DateUtils.toDateString(
            new Date(issue.createTime),
            DateUtils.SHORT_MONTH_DAY_TIME_FORMAT,
          )}
        </Text>
      </IssueProperty>
      <IssueProperty label="Updated">
        <RiTimeLine size={16} aria-hidden />
        <Text as="span" variant="bodySmall">
          {DateUtils.xAgoShort(new Date(issue.updateTime))}
        </Text>
      </IssueProperty>
    </div>
  );
};

export const IssueAutonomyLevel = ({
  canEdit,
  issue,
  trackingContext,
}: IssueControlProps) => {
  const { updateIssue, isUpdating } = useUpdateIssue({
    onError: 'post-alert',
  });
  const trackPropertyUpdate = useTrackIssuePropertyUpdate(issue.name, {
    context: trackingContext,
  });

  return (
    <IssueAutonomyLevelPicker
      value={issue.autonomyLevel}
      disabled={!canEdit || isUpdating}
      onChange={autonomyLevel => {
        trackPropertyUpdate('autonomy-level', autonomyLevel);
        void updateIssue(
          {
            name: issue.name,
            autonomyLevel,
          },
          'autonomyLevel',
        );
      }}
     data-track-source-anchor="a7137050df;issue-autonomy-level-issue-autonomy-level-picker"/>
  );
};

export const IssueSurfaces = ({
  canEdit,
  issue,
  trackingContext,
}: IssueControlProps) => {
  const { updateIssue, isUpdating } = useUpdateIssue({
    onError: 'post-alert',
  });
  const trackPropertyUpdate = useTrackIssuePropertyUpdate(issue.name, {
    context: trackingContext,
  });

  return (
    <IssueSurfacesPicker
      surfaces={(issue.surfaces ?? []).flatMap(surface => {
        const issueSurface = getTypeOrNull(surface);

        return issueSurface ? [issueSurface] : [];
      })}
      disabled={!canEdit || isUpdating}
      onChange={surfaceNames => {
        trackPropertyUpdate(
          'surfaces',
          surfaceNames.length > 0 ? surfaceNames.join(',') : 'none',
        );
        void updateIssue(
          {
            name: issue.name,
            surfaces: surfaceNames,
          },
          'surfaces',
        );
      }}
     data-track-source-anchor="ef40a1657e;issue-surfaces-issue-surfaces-picker"/>
  );
};

export const IssueSurfacesProperty = ({
  canEdit,
  issue,
  trackingContext,
}: IssueControlProps) => (
  <IssueProperty label="Surfaces">
    <IssueSurfaces
      canEdit={canEdit}
      issue={issue}
      trackingContext={trackingContext}
    />
  </IssueProperty>
);
